import React, { useState, useEffect } from 'react';
import moment from 'moment';
import 'moment/locale/th';

// รายละเอียดใบจ่ายของแม่บ้าน (GET /api/cleaner-web/payout/:id) -- เปิดจาก "ประวัติรับเงิน"
// ยอดสุทธิ = payout.total_amount เสมอ (= ยอดงาน + ปรับยอด − หักค่าเสียหาย) · บรรทัดหักค่าเสียหายมาจากระบบคลังสินค้า (deductions)
const baht = (v) => `฿${Number(v || 0).toLocaleString('th-TH', { maximumFractionDigits: 2 })}`;
const errOf = (e) => e?.response?.data?.message || 'โหลดไม่สำเร็จ — สัญญาณเน็ตอาจไม่ดี ลองอีกครั้ง';
const ST = { requested: ['รอโอน (ขอเบิก)', '#B26B0E'], draft: ['รอโอน (ปิดรอบ)', '#2C6DAF'], paid: ['โอนแล้ว', '#0F8A56'], cancelled: ['ยกเลิก', '#8A938B'] };

export default function PayoutDetail({ cw, S, payoutId, onBack }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    moment.locale('th');
    cw(`payout/${payoutId}`).then((r) => setData(r.data)).catch((e) => setError(errOf(e)));
  }, [cw, payoutId]);

  const bar = (
    <div style={S.bar}>
      <button style={S.back} onClick={onBack}>‹ ประวัติ</button>
      <div><h1 style={{ ...S.h, fontSize: 17 }}>รายละเอียดใบจ่าย</h1>
        {data?.payout && <div style={S.sub}>{data.payout.cleaner?.firstname || ''}{data.payout.period_start ? ` · ${moment(data.payout.period_start).format('D MMM')} - ${moment(data.payout.period_end).format('D MMM')}` : ''}</div>}
      </div>
    </div>
  );
  if (error) return <div style={S.page}>{bar}<div style={{ ...S.card, textAlign: 'center', color: '#CD3E33' }}>{error}</div></div>;
  if (!data) return <div style={S.page}>{bar}<div style={{ ...S.card, textAlign: 'center', color: '#8A938B' }}>กำลังโหลด…</div></div>;

  const p = data.payout || {};
  const lines = Array.isArray(data.lines) ? data.lines : [];
  const deductions = Array.isArray(data.deductions) ? data.deductions : [];
  const itemsAmount = p.items_amount !== null && p.items_amount !== undefined ? Number(p.items_amount) : lines.reduce((s, l) => s + (Number(l.amount) || 0), 0);
  const adjust = Number(p.adjust_amount) || 0;
  const deduction = p.deduction_amount !== null && p.deduction_amount !== undefined
    ? Number(p.deduction_amount) : deductions.reduce((s, d) => s + (Number(d.amount) || 0), 0);
  const [label, color] = ST[p.status] || [p.status, '#8A938B'];
  const row = (left, right, sub, key, rightColor) => (
    <div key={key} style={{ display: 'flex', alignItems: 'baseline', gap: 8, padding: '8px 0', borderTop: '1px solid #EEF1EE' }}>
      <div style={{ flex: 1, minWidth: 0 }}><div style={{ fontSize: 15 }}>{left}</div>{sub && <div style={{ fontSize: 12, color: '#586159' }}>{sub}</div>}</div>
      <b style={{ color: rightColor || '#1A211D', whiteSpace: 'nowrap' }}>{right}</b>
    </div>
  );

  return (
    <div style={S.page}>
      {bar}
      <div style={{ ...S.card, background: '#F1F7F3', borderColor: '#0F8A56' }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div style={{ flex: 1, fontSize: 13, color: '#586159' }}>ยอดสุทธิ</div>
          <span style={S.pill(color)}>{label}</span>
        </div>
        <div style={{ fontSize: 30, fontWeight: 800 }}>{baht(p.total_amount)}</div>
        {p.paid_at && <div style={{ fontSize: 12, color: '#586159' }}>โอนเมื่อ {moment(p.paid_at).format('D MMM YY HH:mm')}</div>}
        {p.slip_url && <a href={p.slip_url} target="_blank" rel="noreferrer" style={{ display: 'block', marginTop: 6, fontSize: 14, fontWeight: 700, color: '#2C6DAF' }}>🧾 ดูสลิป</a>}
      </div>

      <div style={S.card}>
        <div style={{ fontWeight: 800, marginBottom: 4 }}>รายการงาน ({lines.length})</div>
        {lines.map((l, i) => row(
          `${l.service_date ? moment(l.service_date).format('D MMM') : ''} · ${l.room_name || '-'}`,
          baht(l.amount),
          [l.contact_name, l.extra ? 'คนช่วยเพิ่ม' : (l.team ? 'งานทีม' : null)].filter(Boolean).join(' · '),
          `${l.kind}-${l.id}-${i}`,
        ))}
        {row(<b>ยอดงาน</b>, baht(itemsAmount), null, 'items')}
        {adjust !== 0 && row(adjust > 0 ? 'เพิ่ม' : 'หัก (ปรับยอด)', `${adjust > 0 ? '+' : '−'}${baht(Math.abs(adjust))}`, p.adjust_note, 'adjust', adjust > 0 ? '#0F8A56' : '#CD3E33')}
        {deductions.map((d) => row(
          `หักค่าเสียหาย · ${d.name || ''}${Number(d.qty) ? ` × ${Number(d.qty)}` : ''}`,
          `−${baht(d.amount)}`,
          [d.incident_no, d.room].filter(Boolean).join(' · '),
          `ded-${d.id || d.incident_no}`,
          '#CD3E33',
        ))}
        {deductions.length === 0 && deduction > 0 && row('หักค่าเสียหาย', `−${baht(deduction)}`, null, 'ded-total', '#CD3E33')}
        <div style={{ display: 'flex', alignItems: 'baseline', paddingTop: 10, marginTop: 4, borderTop: '2px solid #DBE2DC' }}>
          <div style={{ flex: 1, fontWeight: 800 }}>ยอดสุทธิ</div>
          <div style={{ fontSize: 22, fontWeight: 800 }}>{baht(p.total_amount)}</div>
        </div>
        {deductions.length > 0 && <div style={{ fontSize: 12, color: '#586159', marginTop: 6 }}>หักค่าเสียหายเฉพาะรายการที่คุณกดยอมรับแล้ว ไม่เกินเพดานต่อใบ ส่วนที่เหลือยกไปใบถัดไป</div>}
      </div>
    </div>
  );
}
